import { makeBackendRequest, TokenService } from './tokenService';

export interface JsonataMappingRequest {
  jsonataExpression: string;
  inputJson: any;
}

export interface JsonataMappingResponse {
  mappingResult: any;
  schemaValidation: {
    isValid: boolean;
    errors: any[] | null;
  };
}

export class JsonataService {
  /**
   * Execute JSONATA mapping with schema validation
   */
  static async executeMapping(request: JsonataMappingRequest): Promise<JsonataMappingResponse> {
    return await makeBackendRequest('/jsonata/execute-mapping', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

// Suggest JSONATA mapping using AI with SSE
export const suggestMapping = async (inputJsonFiles: any[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      console.log(`🤖 Requesting mapping suggestion for ${inputJsonFiles.length} files...`);

      // Get token for authentication
      const token = TokenService.getToken();
      if (!token) {
        throw new Error('No access token found');
      }

      // Create fetch request with SSE
      const response = fetch(`${process.env.REACT_APP_BACKEND_URL}/jsonata/suggest-mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          inputJsonFiles
        })
      });

      response.then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error('No response body reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              reject(new Error('Stream ended without final result'));
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (data.final && data.suggestedMapping) {
                    console.log('✅ Mapping suggestion received successfully');
                    resolve(data.suggestedMapping);
                    return;
                  } else if (data.status) {
                    console.log(`📢 Progress: ${data.status}`);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse SSE data:', line);
                }
              }
            }

            processStream();
          }).catch(error => {
            console.error('❌ Error reading SSE stream:', error);
            reject(error);
          });
        };

        processStream();
      }).catch(error => {
        console.error('❌ Error in fetch request:', error);
        reject(error);
      });

    } catch (error) {
      console.error('❌ Error suggesting mapping:', error);
      reject(error);
    }
  });
};